// CountdownRedirectModal.jsx
import { useEffect, useRef, useState } from "react";

export default function CountdownRedirectModal({
  show,
  seconds,
  redirectUrl,
  title = "Redirecting",
  message = "Continuing shortly…",
  onHide,
  cancelText = "Cancel",
  proceedText = "Go now",
  replace = false,
  centered = true,
}) {
  const [remaining, setRemaining] = useState(Math.max(0, Math.floor(seconds)));
  const timerRef = useRef(null);
  const redirectedRef = useRef(false);

  // Reset countdown when opened or seconds changes
  useEffect(() => {
    if (show) setRemaining(Math.max(0, Math.floor(seconds)));
  }, [show, seconds]);

  // Drive countdown and redirect at 0
  useEffect(() => {
    if (!show) {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
      return;
    }

    timerRef.current = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          timerRef.current = null;

          if (!redirectedRef.current) {
            redirectedRef.current = true;
            if (replace) {
              window.location.replace(redirectUrl);
            } else {
              window.location.assign(redirectUrl);
            }
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [show, redirectUrl, replace]);

  const handleProceedNow = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    if (!redirectedRef.current) {
      redirectedRef.current = true;
      if (replace) {
        window.location.replace(redirectUrl);
      } else {
        window.location.assign(redirectUrl);
      }
    }
  };

  const handleCancel = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    redirectedRef.current = false;
    onHide?.();
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/40">
      <div
        className={`bg-white rounded-xl shadow-xl w-1/2 h-[170px] p-6 transform transition-all ${
          centered ? "mx-4" : "mt-10 mx-auto"
        }`}
      >
        {/* Header */}
        <div className="text-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
        </div>

        {/* Body */}
        <div className="text-center">
          <p className="text-gray-600 mb-2">{message}</p>
          <p className="text-gray-900 text-lg font-medium">
            Redirecting in{" "}
            <span className="font-bold text-indigo-600">{remaining}</span>{" "}
            second{remaining === 1 ? "" : "s"}…
          </p>
        </div>

        {/* Footer (commented out to keep modal compact) */}
        {/*
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={handleCancel}
            className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition"
          >
            {cancelText}
          </button>
          <button
            onClick={handleProceedNow}
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition shadow-sm"
          >
            {proceedText}
          </button>
        </div>
        */}
      </div>
    </div>
  );
}
